// Updated: max upload size set to 10MB, expanded MIME types, improved WebP conversion with fallback
const ALLOWED_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
  'image/heif',
  'image/bmp',
  'image/tiff',
];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

const MAX_DIMENSION = 3000;

export const validateImage = (file: File): string | null => {
  const isImage = file.type.startsWith('image/') || /\.(jpe?g|png|webp|heic|heif|bmp|tiff?)$/i.test(file.name);
  if (!isImage) {
    return 'Please select an image file (JPEG, PNG, WebP, or HEIC)';
  }

  if (file.size > MAX_FILE_SIZE) {
    return 'Image size must be less than 10MB';
  }

  return null;
};

export const convertToWebP = async (file: File): Promise<File> => {
  if (file.type === 'image/webp' && file.size < 2 * 1024 * 1024) {
    return file;
  }

  return new Promise((resolve, reject) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);

      let { width, height } = img;
      if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
        const ratio = Math.min(MAX_DIMENSION / width, MAX_DIMENSION / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        reject(new Error('Could not create canvas context'));
        return;
      }

      ctx.drawImage(img, 0, 0, width, height);

      const tryFormat = (format: string, quality: number): Promise<Blob | null> => {
        return new Promise((res) => {
          canvas.toBlob(
            (blob) => res(blob),
            format,
            quality
          );
        });
      };

      (async () => {
        let blob = await tryFormat('image/webp', 0.82);

        if (!blob || blob.type !== 'image/webp') {
          blob = await tryFormat('image/jpeg', 0.85);
        }

        if (!blob) {
          reject(new Error('Failed to convert image. Please try a JPEG or PNG file.'));
          return;
        }

        const ext = blob.type === 'image/webp' ? 'webp' : 'jpg';
        const converted = new File(
          [blob],
          `${file.name.split('.')[0]}.${ext}`,
          { type: blob.type }
        );
        resolve(converted);
      })();
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Failed to load image. The file may be corrupted or in an unsupported format.'));
    };

    img.src = objectUrl;
  });
};